import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal, LineReveal } from "@/components/Reveal";

const CHAPTERS = [
  { n: "01", t: "27 years in the room.", d: "Nearly three decades leading HR through hiring freezes, restructures, investigations, and pay disputes, in the public sector and beyond. There's very little I haven't seen up close." },
  { n: "02", t: "Public sector at the core.", d: "I built my career in public-sector HR leadership, where transparency, comparator-based pay, and procurement scrutiny are the norm. That rigor now benefits every client I serve." },
  { n: "03", t: "Oregon, specifically.", d: "This isn't generic HR advice with an Oregon sticker on it. ORS 652.220, BOLI rules, Oregon leave law, this is the terrain I know cold." },
  { n: "04", t: "Plain-spoken by design.", d: "Compliance shouldn't require a law degree to understand. I translate the rules into decisions you can actually make, warmly and without the jargon." },
];

const About = () => {
  const ref = useRef(null);

  return (
    <div data-testid="about-page">
      <section ref={ref} className="relative min-h-[70vh] flex items-end overflow-hidden grain">
        <div className="absolute inset-0">
          <img src="/generated/about.png" alt="Old-growth Oregon trail" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-ink/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40 w-full">
          <p className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-6">About Susan</p>
          <h1 className="font-serif text-white text-5xl md:text-8xl leading-[0.95] tracking-tight">
            <LineReveal lines={["A path already", "walked."]} delay={0.2} />
          </h1>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <p className="font-serif text-3xl md:text-4xl text-sage-100 leading-snug mb-8">
            I'm Susan, founder of Cascade HR Advisors. After 27 years leading HR, I started this practice to do one thing exceptionally well: help Oregon employers get pay equity and people decisions right.
          </p>
          <p className="text-sage-300 text-lg leading-relaxed">
            Most HR help is either a compliance report you can't act on, or a consultant who's never seen your kind of organization. I built Cascade to be neither. I bring the rigor of public-sector HR leadership and pair it with plain-language guidance you can actually use, whether you're a solo owner or an HR director at a mid-size employer.
          </p>
        </Reveal>
      </section>

      <section className="bg-forest-800/70 border-y border-forest-700 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-4">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 items-start border-t border-forest-700 pt-10 pb-6">
                <div className="md:col-span-3"><span className="font-serif text-7xl md:text-8xl text-golden-500/25">{c.n}</span></div>
                <div className="md:col-span-8 md:col-start-5">
                  <h3 className="font-serif text-3xl md:text-4xl text-sage-100 mb-3">{c.t}</h3>
                  <p className="text-sage-300 text-lg leading-relaxed max-w-2xl">{c.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 text-center py-24">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl text-sage-100 mb-8">Let's talk about your situation.</h2>
          <a href="/contact" data-testid="about-cta" className="group inline-flex items-center gap-2 bg-golden-500 text-navy font-semibold px-10 py-4 rounded-sm transition-colors duration-300 hover:bg-golden-600">
            Book a discovery call <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
